import { customAlphabet, nanoid } from 'nanoid';
import { monotonicFactory, ulid } from 'ulid';

/**
 * NanoID + ULID generators. Deliberately a single library file (not
 * one per algo) because the tool page is a 2-tab layout sharing the
 * `count` and "全部复制" affordances.
 */

export interface NanoIdOpts {
  /** [4, 64]. */
  length: number;
  /**
   * Optional alphabet. Default is nanoid's URL-safe 64-char set.
   * When supplied, must contain ≥ 2 distinct characters; duplicates
   * are an error rather than silently de-duped because callers
   * almost certainly mistyped if they pass them.
   */
  alphabet?: string;
  /** [1, 1000]. */
  count: number;
}

export interface UlidOpts {
  count: number;
  /** When true, consecutive ulids are guaranteed lexicographically increasing. */
  monotonic: boolean;
}

const NANOID_DEFAULT_ALPHABET =
  'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';

export const NANOID_PRESETS: ReadonlyArray<{ id: string; label: string; alphabet: string }> = [
  { id: 'urlsafe', label: 'URL-safe (默认)', alphabet: NANOID_DEFAULT_ALPHABET },
  { id: 'numbers', label: '数字 0-9', alphabet: '0123456789' },
  { id: 'lower', label: '小写 a-z', alphabet: 'abcdefghijklmnopqrstuvwxyz' },
  {
    id: 'alnum',
    label: '字母+数字',
    alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
  },
];

export function generateNanoIds(opts: NanoIdOpts): string[] {
  const length = clamp(opts.length, 4, 64);
  const count = clamp(opts.count, 1, 1000);

  if (opts.alphabet !== undefined) {
    validateAlphabet(opts.alphabet);
    const fn = customAlphabet(opts.alphabet, length);
    return Array.from({ length: count }, () => fn());
  }
  // Use the bundled `nanoid(size)` for the default alphabet — slightly
  // smaller bundle path than customAlphabet(default).
  return Array.from({ length: count }, () => nanoid(length));
}

function validateAlphabet(alphabet: string): void {
  // Iterate code points (handles surrogate pairs / emoji defensively).
  const chars = Array.from(alphabet);
  if (chars.length < 2) {
    throw new Error('alphabet: 至少需要 2 个不同字符');
  }
  if (new Set(chars).size !== chars.length) {
    throw new Error('alphabet: 含重复字符');
  }
}

export function generateUlids(opts: UlidOpts): string[] {
  const count = clamp(opts.count, 1, 1000);
  if (opts.monotonic) {
    const m = monotonicFactory();
    return Array.from({ length: count }, () => m());
  }
  return Array.from({ length: count }, () => ulid());
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  const x = Math.floor(n);
  if (x < lo) return lo;
  if (x > hi) return hi;
  return x;
}
